/**
 * Admin Companies Tab Component
 */
import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  Typography,
  Chip,
  Alert,
} from '@mui/material';
import { Add, Edit, CheckCircle, Cancel } from '@mui/icons-material';
import { adminApi, AdminCompany } from '@/api/admin';
import { DATA_OPERATOR_ORG_ID } from '@/constants/fieldVisibility';

const isCoreOperatorCompany = (companyId: string) => companyId === DATA_OPERATOR_ORG_ID;

export const AdminCompaniesTab: React.FC = () => {
  const [companies, setCompanies] = useState<AdminCompany[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCompany, setEditingCompany] = useState<AdminCompany | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    is_active: true,
    validate_through_database: false,
  });

  useEffect(() => {
    loadCompanies();
  }, []);

  const loadCompanies = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await adminApi.getCompanies();
      setCompanies(data || []);
    } catch (err: any) {
      console.error('Error loading companies:', err);
      setError(err.response?.data?.detail || err.message || 'Failed to load companies');
      setCompanies([]); // Ensure companies is always an array
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (company?: AdminCompany) => {
    if (company) {
      setEditingCompany(company);
      setFormData({
        name: company.name,
        description: company.description || '',
        is_active: company.is_active,
        validate_through_database: isCoreOperatorCompany(company.id)
          ? true
          : Boolean(company.validate_through_database),
      });
    } else {
      setEditingCompany(null);
      setFormData({ name: '', description: '', is_active: true, validate_through_database: false });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingCompany(null);
    setFormData({ name: '', description: '', is_active: true, validate_through_database: false });
  };

  const handleSave = async () => {
    try {
      if (editingCompany) {
        await adminApi.updateCompany(editingCompany.id, formData);
      } else {
        await adminApi.createCompany(formData);
      }
      handleCloseDialog();
      loadCompanies();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to save company');
    }
  };

  const handleToggleValidation = async (company: AdminCompany) => {
    if (isCoreOperatorCompany(company.id)) {
      return;
    }
    try {
      await adminApi.updateCompany(company.id, {
        validate_through_database: !company.validate_through_database,
      });
      loadCompanies();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to update validation access');
    }
  };

  const handleToggleActive = async (company: AdminCompany) => {
    try {
      await adminApi.updateCompany(company.id, { is_active: !company.is_active });
      loadCompanies();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to update company');
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5">Companies Management</Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => handleOpenDialog()}
        >
          Add Company
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Users</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Validate through database</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center">Loading...</TableCell>
              </TableRow>
            ) : !companies || companies.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">No companies found</TableCell>
              </TableRow>
            ) : (
              companies.map((company) => {
                const validationAlwaysOn = isCoreOperatorCompany(company.id);
                const validationOn = validationAlwaysOn || Boolean(company.validate_through_database);
                return (
                <TableRow key={company.id}>
                  <TableCell>{company.name}</TableCell>
                  <TableCell>{company.description || '-'}</TableCell>
                  <TableCell>{company.user_count || 0}</TableCell>
                  <TableCell>
                    <Chip
                      label={company.is_active ? 'Active' : 'Inactive'}
                      color={company.is_active ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Switch
                      size="small"
                      checked={validationOn}
                      disabled={validationAlwaysOn}
                      onChange={() => handleToggleValidation(company)}
                      inputProps={{ 'aria-label': 'Validate through database' }}
                    />
                    {validationAlwaysOn ? (
                      <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                        Always on
                      </Typography>
                    ) : null}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={() => handleToggleActive(company)}
                      title={company.is_active ? 'Deactivate' : 'Activate'}
                    >
                      {company.is_active ? <CheckCircle color="success" /> : <Cancel />}
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleOpenDialog(company)}
                      title="Edit"
                    >
                      <Edit />
                    </IconButton>
                  </TableCell>
                </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingCompany ? 'Edit Company' : 'Create Company'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="Description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              fullWidth
              multiline
              rows={3}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                />
              }
              label="Active"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={
                    editingCompany && isCoreOperatorCompany(editingCompany.id)
                      ? true
                      : formData.validate_through_database
                  }
                  disabled={Boolean(editingCompany && isCoreOperatorCompany(editingCompany.id))}
                  onChange={(e) =>
                    setFormData({ ...formData, validate_through_database: e.target.checked })
                  }
                />
              }
              label={
                editingCompany && isCoreOperatorCompany(editingCompany.id)
                  ? 'Validate through database (always on for this company)'
                  : 'Validate through database'
              }
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" disabled={!formData.name}>
            {editingCompany ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
